import type {
  GetHandwrittenNoteUrlsData,
  IssuePresignedUrlData,
  IssuePresignedUrlPayload,
  IssueTempPresignedUrlData,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'

import {
  adaptHandwrittenNotePresignedUrls,
  adaptHandwrittenNoteUrls,
  adaptTempPresignedUrl,
} from './adapters'
import type { HandwrittenNoteKey, HandwrittenNotePresignedUrl, HandwrittenNoteUrl } from './schemas'

// 필기 JSON 자체는 S3 주소로 직접 읽고 쓴다. 여기 있는 것은 그 주소를 받는 메인 서버 요청뿐이다.

/**
 * 필기 캔버스 하나의 JSON 주소. 서버는 subIds·tags 를 배열로 받아 조합 전체를 내지만, 캔버스 하나만 물으므로
 * 두 값을 하나씩 싣는다 — 앱마다 배열 직렬화 형식(쉼표·반복)이 달라도 와이어가 같아진다.
 * 응답은 CommonResponse 봉투 없이 온다.
 */
export const getHandwrittenNoteUrl = async (
  { id, subId, type, tag }: HandwrittenNoteKey,
  meta?: RequestMeta,
): Promise<HandwrittenNoteUrl['url'] | undefined> => {
  const body = await httpClient().get<GetHandwrittenNoteUrlsData>('/v1/handwritten-notes', {
    params: { type, id, subIds: subId, tags: tag },
    meta,
  })
  return adaptHandwrittenNoteUrls(body)[0]?.url
}

/** 필기를 올릴 presigned 주소를 한꺼번에 받는다. 주소는 서명이 붙어 7일간 쓸 수 있다 */
export const issueHandwrittenNotePresignedUrls = async (
  keys: HandwrittenNoteKey[],
  meta?: RequestMeta,
): Promise<HandwrittenNotePresignedUrl[]> => {
  const body = await httpClient().post<IssuePresignedUrlData>(
    '/handwritten-note/presigned-url',
    keys satisfies IssuePresignedUrlPayload,
    { meta },
  )
  return adaptHandwrittenNotePresignedUrls(body.data) ?? []
}

/**
 * 키와 무관한 임시 업로드 주소 하나. 서버가 키를 받지 않고 임의 경로로 발급한다.
 * 발급하지 못하면 null 이다.
 */
export const issueTempHandwrittenNotePresignedUrl = async (
  meta?: RequestMeta,
): Promise<HandwrittenNotePresignedUrl['presignedUrl']> => {
  const body = await httpClient().post<IssueTempPresignedUrlData>(
    '/handwritten-note/presigned-url/temp',
    undefined,
    { meta },
  )
  return adaptTempPresignedUrl(body.data)
}
